import * as Sentry from '@sentry/react';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { Suspense, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getPhone } from '@/lib/getPhone.ts';
import RestaurantName, { RestaurantNameLoader } from '../RestaurantName/RestaurantName.tsx';
import { ApiError } from '@/lib/ApiError.ts';
import {
  AuthAtom,
  RestaurantAtom,
  UserAtom,
  useWidgetState,
  useRestaurantId,
  widgetUpdateUserRejection,
  resolveWidgetMarketingSmsOptIn,
} from '../lib/state.ts';
import { acceptTerms, updateUser, verification, verify } from '@/lib/api.ts';
import { recaptchaTokenFor } from '@/lib/recaptcha.ts';
import { identify, track, resolveBookingType } from '@/lib/analytics/index.ts';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { OTPInput } from '@dorsia/web-design-system/src/atoms/OTPInput';
import { Badge } from '@dorsia/web-design-system/src/atoms/Badge';
import debounce from 'lodash/debounce';
import { useAnnounce } from '../lib/announcer.ts';

import styles from './VerifyPage.module.css';
import eventStyles from '../EventPage/EventPage.module.css';

const VerifyPage: React.FC = () => {
  const navigate = useNavigate();
  const [sending, setSending] = useState<boolean | null>(null);
  const [widget, updateWidget] = useWidgetState();
  const setAuth = useSetAtom(AuthAtom);
  const setUser = useSetAtom(UserAtom);
  const restaurant = useAtomValue(RestaurantAtom);
  const routeRestaurantId = useRestaurantId();

  const isZeroDollarNoPayment =
    widget.bookingType === 'event' &&
    (widget.option?.price ?? -1) === 0 &&
    !!restaurant?.allow_zero_dollar_booking_links_without_payment_method;
  const [otp, setOtp] = useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();

  const onResendClick = (event: React.SyntheticEvent) => {
    event.preventDefault();
    void onResend();
  };

  const onResend = async () => {
    updateWidget({
      phone_confirmed: null,
    });

    setSending(true);

    const recaptchaToken = await recaptchaTokenFor('send_code', executeRecaptcha);
    await verify(widget.phone_number!, '', recaptchaToken);

    setSending(false);

    setTimeout(() => setSending(null), 5000);
  };

  const onComplete = async (code: string) => {
    try {
      const recaptchaTokenVerify = await recaptchaTokenFor('verify_code', executeRecaptcha);
      // US-only opt-in gate lives in `resolveWidgetMarketingSmsOptIn` so this
      // callsite cannot drift from DetailsPage's checkbox render condition
      // [GRO-7075].
      const marketingSmsOptIn = resolveWidgetMarketingSmsOptIn(widget);
      const verification_response = await verification(
        widget.phone_number!,
        code,
        recaptchaTokenVerify,
        marketingSmsOptIn
      );

      if (!verification_response?.token || !verification_response?.user) {
        updateWidget({ phone_confirmed: false });
        setOtp('');
        return;
      }

      Sentry.setUser({
        id: verification_response.user,
      });

      // Link this anonymous widget session to the member (id only for now).
      // Done before the `Widget Verify Completed` track below so that event —
      // and every later one (payment, booking) — carries the user id, and so
      // the anonymous top-of-funnel events fired earlier merge into the member's
      // profile downstream (Mixpanel et al.). `verification_response.user` is
      // the member uuid, matching the identifier the member app identifies with.
      // Profile traits are set further down from the validated `updateUser`
      // response, not the raw (unvalidated) widget input.
      identify(verification_response.user);

      if (routeRestaurantId != null) {
        track('Widget Verify Completed', {
          restaurant_id: routeRestaurantId,
          booking_type: resolveBookingType(widget.bookingType),
          is_new_user: !!verification_response.new,
          marketing_sms_opt_in: marketingSmsOptIn,
        });
      }

      setAuth(verification_response);

      // Back the DetailsPage "By clicking Continue, you agree to our Terms of
      // Use and Privacy Policy" copy for guests who first hit /verify (i.e.
      // did not have a Sanctum token when they left DetailsPage). Without this
      // call, brand-new widget bookers reach the Member app never having
      // stamped `accepted_terms_at` and get blocked on the terms modal on
      // first launch [GRO-7075]. The endpoint is strictly append-once (see
      // UserController::acceptTerms + AcceptTermsEndpointTest) so this is
      // safe on every verification. Fire-and-forget: a stamp failure must
      // never block the booking, and Sentry surfaces any regressions.
      acceptTerms(verification_response.token).catch((e: unknown) => {
        Sentry.captureException(e, { tags: { source: 'widget_verify_accept_terms' } });
      });

      let user_response;
      const basePayload = {
        first_name: widget.first_name!,
        last_name: widget.last_name!,
        email: widget.email!,
      };

      // Best-effort referral code association. The referral code comes from a
      // URL param (?code=SUMMER25) set by a promoter — if the code is invalid,
      // deactivated, or the user already has a different one, the API throws
      // InvalidReferralCodeException (422). We retry without used_referral_code
      // so a bad/expired promo link never blocks the user from completing their
      // booking. If the failure is non-referral (network, validation, etc.), the
      // retry also fails and the outer catch handles it normally.
      if (widget.referral_code) {
        try {
          user_response = await updateUser(verification_response.token, {
            ...basePayload,
            used_referral_code: widget.referral_code,
          });
        } catch {
          user_response = await updateUser(verification_response.token, basePayload);
        }
      } else {
        user_response = await updateUser(verification_response.token, basePayload);
      }

      setUser({
        first_name: user_response.user.first_name,
        last_name: user_response.user.last_name,
        email: user_response.user.email,
        phone: widget.phone_number,
        referral_code: widget.referral_code,
      });

      // Enrich the member's profile with the server-validated traits (the API
      // may normalize or reject the raw widget input), keyed to the same uuid.
      identify(verification_response.user, {
        first_name: user_response.user.first_name,
        last_name: user_response.user.last_name,
        email: user_response.user.email,
        phone: widget.phone_number,
      });

      updateWidget({
        phone_confirmed: true,
      });

      if (widget.returnTo) {
        const returnTo = widget.returnTo;
        updateWidget({ returnTo: undefined });
        setTimeout(() => navigate(`../${returnTo}`), 500);
      } else {
        const nextRoute =
          widget.bookingType === 'custom_request'
            ? '../request/steps'
            : isZeroDollarNoPayment
              ? '../checkout'
              : '../payment';
        setTimeout(() => navigate(nextRoute), 500);
      }
    } catch (e: unknown) {
      if (e instanceof ApiError && e.type === 'UpdateUserErrorResponse') {
        // Carries the message AND any did-you-mean suggestion back to the only
        // page with an editable email field [GRO-6429]. Both live on the widget
        // state because this navigation is the boundary they must survive.
        // The typed email comes along so the copy can name the domain the guest
        // actually entered; the API answer describes the rejection, not the input.
        updateWidget(widgetUpdateUserRejection(e, widget.email));

        navigate(`../details`);
      } else if (e instanceof Error) {
        Sentry.captureException(e);
        console.error(e.message);
      }

      updateWidget({ phone_confirmed: false });
      setOtp('');
    }
  };

  useEffect(() => {
    if (otp.length === 4) {
      debouncedAuth(otp);
    }
  }, [otp]);

  // Announce a failed verification to assistive tech (no-op in the SPA).
  const announce = useAnnounce();
  useEffect(() => {
    if (widget.phone_confirmed === false) {
      announce('The code you entered is incorrect');
    }
  }, [widget.phone_confirmed, announce]);

  const debouncedAuth = useCallback(debounce(onComplete, 300), []);
  const phone = getPhone(widget.phone_number, widget.phone_country);

  if (!phone) {
    return <></>;
  }

  return (
    <div className={eventStyles.eventContent}>
      <div className={eventStyles.card}>
        <div className={styles.restaurantTitle}>
          <Suspense fallback={<RestaurantNameLoader />}>
            <RestaurantName />
          </Suspense>
          {restaurant?.type === 'event' && <Badge variant="event">EVENT</Badge>}
        </div>

        <p className={styles.subtitle}>
          We sent a 4-digit code to <strong>{!!phone && phone.formatNational()}</strong>
        </p>

        <div className={styles.otpContainer}>
          <OTPInput length={4} value={otp} onChange={setOtp} />
        </div>

        {widget.phone_confirmed === false && (
          <div className={styles.error}>The code you entered is incorrect</div>
        )}

        <div className={styles.resendLink}>
          {sending === true && <span>Sending...</span>}
          {sending === false && <span>Sent</span>}
          {sending === null && (
            <span>
              Didn't receive a text or call?{' '}
              <a href="/" onClick={onResendClick}>
                Resend
              </a>
            </span>
          )}
        </div>

        <Link to="../details" className={styles.backLink}>
          Back
        </Link>
      </div>
    </div>
  );
};

export default VerifyPage;
